import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import DragDropFileUpload from './DragDropFileUpload';

interface IssueForm {
  subject: string;
  description: string;
  attachments?: FileList | null | undefined;
}

interface SubmitTicketModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (values: IssueForm, formikBag: any) => Promise<void>;
}

const validationSchema = Yup.object({
  subject: Yup.string()
    .required('Subject is required')
    .min(5, 'Subject must be at least 5 characters')
    .max(100, 'Subject must be less than 100 characters'),
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(1000, 'Description must be less than 1000 characters'),
  attachments: Yup.mixed()
    .test('fileSize', 'Each file must be smaller than 10MB', function(value) {
      if (!value) return true;
      const files = Array.from(value as FileList);
      return files.every(file => file.size <= 10 * 1024 * 1024);
    })
    .test('fileCount', 'Maximum 5 files allowed', function(value) {
      if (!value) return true;
      const files = Array.from(value as FileList);
      return files.length <= 5;
    })
    .test('fileType', 'Only PDF, DOC, TXT, and image files are allowed', function(value) {
      if (!value) return true;
      const files = Array.from(value as FileList);
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif'
      ];
      return files.every(file => allowedTypes.includes(file.type));
    })
});

const SubmitTicketModal: React.FC<SubmitTicketModalProps> = ({
  show,
  onHide,
  onSubmit
}) => {
  const inputStyle = {
    borderRadius: '8px',
    border: '2px solid #e9ecef',
    padding: '12px 16px',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    backgroundColor: '#fff'
  };

  const labelStyle = {
    fontWeight: '600',
    color: '#495057',
    marginBottom: '8px'
  };

  const handleInputFocus = (e: any) => {
    e.target.style.border = '2px solid #3957A0';
    e.target.style.boxShadow = '0 0 0 0.2rem rgba(57, 87, 160, 0.25)';
  };

  const handleInputBlur = (e: any) => {
    e.target.style.border = '2px solid #e9ecef';
    e.target.style.boxShadow = 'none';
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="submit-issue-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <Modal.Header closeButton style={{backgroundColor: '#f8f9fa', borderBottom: '2px solid #3957A0'}}>
          <Modal.Title style={{color: '#3957A0', fontWeight: 'bold', fontSize: '1.5rem'}}>
            <i className="fas fa-ticket-alt me-2" style={{color: '#D7A360'}}></i>
            Submit an Issue
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: '2rem', backgroundColor: '#fff'}}>
          <Formik
            initialValues={{ subject: '', description: '', attachments: undefined }}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting, setFieldValue, values, errors, touched }) => (
              <FormikForm>
                <div className="form-section mb-4">
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Subject *</Form.Label>
                    <Field name="subject">
                      {({ field, meta }: any) => (
                        <>
                          <Form.Control
                            {...field}
                            type="text"
                            placeholder="Briefly describe your issue"
                            isInvalid={meta.touched && meta.error}
                            style={inputStyle}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                          />
                          <ErrorMessage name="subject" component="div" className="invalid-feedback" />
                        </>
                      )}
                    </Field>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Description *</Form.Label>
                    <Field name="description">
                      {({ field, meta }: any) => (
                        <>
                          <Form.Control
                            {...field}
                            as="textarea"
                            rows={4}
                            placeholder="Provide detailed information about your issue"
                            isInvalid={meta.touched && meta.error}
                            style={{...inputStyle, minHeight: '120px'}}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                          />
                          <ErrorMessage name="description" component="div" className="invalid-feedback" />
                        </>
                      )}
                    </Field>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>
                      Attachments <small className="text-muted">(Optional)</small>
                    </Form.Label>
                    <DragDropFileUpload
                      onFilesSelected={(files) => setFieldValue('attachments', files)}
                      currentFiles={values.attachments}
                      error={touched.attachments && errors.attachments ? String(errors.attachments) : undefined}
                    />
                  </Form.Group>
                </div>

                <div className="d-flex justify-content-center pt-3" style={{borderTop: '1px solid #e9ecef'}}>
                  <Button
                    variant="secondary"
                    onClick={onHide}
                    disabled={isSubmitting}
                    style={{
                      marginRight: '12px',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      border: '2px solid #6c757d',
                      backgroundColor: 'transparent',
                      color: '#6c757d'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      padding: '12px 32px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      backgroundColor: '#3957A0',
                      border: '2px solid #3957A0',
                      color: 'white',
                      minWidth: '140px'
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Submitting...
                      </>
                    ) : (
                      'Submit Issue'
                    )}
                  </Button>
                </div>
              </FormikForm>
            )}
          </Formik>
        </Modal.Body>
      </motion.div>
    </Modal>
  );
};

export default SubmitTicketModal;